import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./ModelsLibraryView.module.scss";
import { View, ViewHeader } from "@/components";
import { ModelsGrid } from "../models-grid";
import { ModelsLibraryToolbar } from "../models-library-toolbar";
import { CreateCatalogModelDrawer } from "../create-catalog-model-drawer/CreateCatalogModelDrawer.comp";
import type { ModelsLibraryFilter } from "../../_constants/models-library-toolbar.const";
import { axiosInstance } from "@/helpers/axios";
import { MODEL_PREVIEW_APP_BASE } from "@/constants/model-preview-app.const";
import type { ModelsLibraryItem } from "../../_types/content-models.types";
import { formatSpeciesDisplayName } from "../../_constants/content-models-ui.const";

function libraryMatchesFilter(model: ModelsLibraryItem, filter: ModelsLibraryFilter): boolean {
  if (filter === "all") return true;
  const s = model.libraryDisplayStatus;
  if (filter === "draft") return s === "draft" || s === "processing";
  if (filter === "in_review") return s === "in_review";
  if (filter === "active") return s === "active";
  return true;
}

export const ModelsLibraryView = () => {
  const [filter, setFilter] = useState<ModelsLibraryFilter>("all");
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [catalogRows, setCatalogRows] = useState<ModelsLibraryItem[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const loadCatalog = useCallback(async () => {
    setCatalogError(null);
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/v2/models/catalog");
      if (!data?.success || !data?.data?.items) {
        setCatalogRows([]);
        return;
      }
      const items = data.data.items as Array<{
        slug: string;
        displayName: string;
        versionLabel: string | null;
        status: string | null;
        previewVersionId: string | null;
        previewVersionStatus: string | null;
      }>;
      const mapped: ModelsLibraryItem[] = items.map((row) => {
        const libraryDisplayStatus =
          row.previewVersionStatus ?? row.status ?? "draft";
        const displayName = formatSpeciesDisplayName(row.displayName);
        return {
          slug: row.slug,
          displayName,
          species: displayName,
          liveVersionLabel: row.versionLabel ?? "—",
          updatedAt: "—",
          updatedBy: "—",
          libraryDisplayStatus,
          previewVersionId: row.previewVersionId,
          previewVersionStatus: row.previewVersionStatus,
        };
      });
      setCatalogRows(mapped);
    } catch {
      setCatalogError("Could not load catalog. Try again in a moment.");
      setCatalogRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  const filtered = useMemo(() => {
    if (filter === "all") return catalogRows;
    return catalogRows.filter((m) => libraryMatchesFilter(m, filter));
  }, [filter, catalogRows]);

  return (
    <View>
      <div className={styles["comp__content"]}>
        <ViewHeader
          heading="3D model library"
          description={`Browse catalog species and active versions. Use <strong>Add new model</strong> to upload a new catalog version (pipeline + review).<br />For a quick local file test (no upload), open <a href="${MODEL_PREVIEW_APP_BASE}/local" target="_blank" rel="noopener noreferrer">Model preview</a> in a new tab.`}
        />

        <ModelsLibraryToolbar
          filter={filter}
          onFilterChange={setFilter}
          onAddModelClick={() => setCatalogOpen(true)}
        />

        {catalogError ? <p className={styles["comp__tip"]}>{catalogError}</p> : null}

        <ModelsGrid models={filtered} loading={loading} />

        <CreateCatalogModelDrawer
          isOpen={catalogOpen}
          onClose={() => setCatalogOpen(false)}
          onCreated={() => void loadCatalog()}
        />
      </div>
    </View>
  );
};
